import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { useAppTFunction } from '@retail/app/i18n';
import { useCustomerAssortments } from '@retail/app/stores/selected-context';
import { NoTableRowsOverlay } from '@retail/components';
import { CustomerAssortmentSelect } from '@retail/customer-discount/components';
import { CreateCustomerContainer } from '@retail/customer-discount/containers';
import { customerAssortmentIdSearchParam } from '@retail/utils';
import { CustomerAssortmentPrices } from './CustomerAssortmentPrices';
import { NoSelectedCustomerAssortment } from './NoSelectedCustomerAssortment';

export const CustomerAssortmentPricesContainer = () => {
  const t = useAppTFunction();
  const customerAssortments = useCustomerAssortments();
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedAssortmentIdParam = searchParams.get(customerAssortmentIdSearchParam);
  const selectedCustomerAssortment = customerAssortments.find(
    ({ assortmentId }) => `${assortmentId}` === selectedAssortmentIdParam
  );

  const selectAssortment = useCallback(
    (assortmentId: number | null, replace = false) =>
      setSearchParams(
        (prevParams) => {
          const nextParams = new URLSearchParams(prevParams);
          if (assortmentId) nextParams.set(customerAssortmentIdSearchParam, `${assortmentId}`);
          else nextParams.delete(customerAssortmentIdSearchParam);
          return nextParams;
        },
        { replace }
      ),
    [setSearchParams]
  );

  // Only when the param is absent: a newly created customer's id is put in the URL before
  // my-assortments has refetched, and must not be overwritten by the previous single option.
  const onlyCustomerAssortmentId =
    customerAssortments.length === 1 ? customerAssortments[0].assortmentId : undefined;
  useEffect(() => {
    if (onlyCustomerAssortmentId && selectedAssortmentIdParam === null) {
      selectAssortment(onlyCustomerAssortmentId, true);
    }
  }, [onlyCustomerAssortmentId, selectedAssortmentIdParam, selectAssortment]);

  if (!customerAssortments.length) {
    return (
      <NoTableRowsOverlay
        noRowsText={t('app.customerAssortments.noCustomers')}
        addAction={<CreateCustomerContainer />}
      />
    );
  }

  if (!selectedCustomerAssortment) {
    return (
      <NoSelectedCustomerAssortment
        customerAssortmentSelect={
          <CustomerAssortmentSelect
            customerAssortments={customerAssortments}
            selectedAssortmentId={null}
            selectAssortment={selectAssortment}
          />
        }
      />
    );
  }

  return (
    <CustomerAssortmentPrices
      customerAssortment={selectedCustomerAssortment}
      customerAssortments={customerAssortments}
      selectAssortment={selectAssortment}
      selectedCustomerAssortment={selectedCustomerAssortment}
    />
  );
};
